import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { notificationsAPI } from '../../services/api';
import { useSnackbar } from '../Snackbar';
import './NotificationBell.css';

const STATUS_LABEL = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  need_info: 'Need Info',
  wont_fix: "Won't Fix",
};

const POLL_INTERVAL_MS = 30_000;

const buildStatusParts = (n) => ({
  context: [n.report_modality, n.report_body_part].filter(Boolean).join(' · '),
  statusLabel: STATUS_LABEL[n.status] || n.status,
});


const buildStatusMessage = (n) => {
  const { context, statusLabel } = buildStatusParts(n);
  const base = context
    ? `Your feedback on ${context} is now ${statusLabel}.`
    : `Your feedback is now ${statusLabel}.`;
  return n.note ? `${base} ${n.note}` : base;
};

const NotificationBell = () => {
  const showToast = useSnackbar();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);

  const seenIdsRef = useRef(new Set());

  const initializedRef = useRef(false);
  const prevUnreadRef = useRef(0);

  
  const goToNotification = useCallback((n) => {
    if (!n.is_read) {
      notificationsAPI.markRead(n.id).catch(() => {});
      setItems(list => list.map(i => (i.id === n.id ? { ...i, is_read: true } : i)));
      setUnread(u => Math.max(0, u - 1));
    }
    setOpen(false);
    navigate(n.report_id ? `/reports/${n.report_id}` : '/feedback');
  }, [navigate]);

  const hasRunLoginSummaryRef = useRef(false);
  useEffect(() => {
    if (hasRunLoginSummaryRef.current) return;
    hasRunLoginSummaryRef.current = true;

    notificationsAPI.getAll()
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        list.forEach(n => seenIdsRef.current.add(n.id));
        const unreadCount = list.filter(n => !n.is_read).length;
        prevUnreadRef.current = unreadCount;
        if (unreadCount > 0) {
          showToast(
            `You have ${unreadCount} new update${unreadCount > 1 ? 's' : ''}. Click to view.`,
            'info',
            6000,
            () => { setOpen(true); fetchList(); }
          );
        }
      })
      .catch(() => {})
      .finally(() => { initializedRef.current = true; });
  }, []);

  const fetchUnread = useCallback(() => {
    notificationsAPI.getUnreadCount()
      .then(async (r) => {
        const count = r.data?.count || 0;
        setUnread(count);

      
        if (initializedRef.current && count > prevUnreadRef.current) {
          try {
            const listRes = await notificationsAPI.getAll();
            const list = Array.isArray(listRes.data) ? listRes.data : [];
            list
              .filter(n => !seenIdsRef.current.has(n.id))
              .forEach(n => {
                seenIdsRef.current.add(n.id);
                showToast(`${buildStatusMessage(n)} Click to view.`, 'info', 6000, () => goToNotification(n));
              });
            list.forEach(n => seenIdsRef.current.add(n.id));
          } catch { /* noop */ }
        }
        prevUnreadRef.current = count;
      })
      .catch(() => {});
  }, [showToast, goToNotification]);

  useEffect(() => {
    const interval = setInterval(fetchUnread, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const fetchList = useCallback(() => {
    setRefreshing(true);
    notificationsAPI.getAll()
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        list.forEach(n => seenIdsRef.current.add(n.id));
        setItems(list);
        setLoaded(true);
      })
      .catch(() => setLoaded(true))
      .finally(() => setRefreshing(false));
  }, []);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchList(); // always refetch on open so status updates show without a page reload
  };

  const handleItemClick = (n) => goToNotification(n);

  const handleMarkAll = () => {
    notificationsAPI.markAllRead().catch(() => {});
    setItems(list => list.map(i => ({ ...i, is_read: true })));
    setUnread(0);
  };

  const fmt = (iso) => new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button className="notif-bell-btn" onClick={handleToggle} title="Notifications">
        <NotificationsOutlinedIcon sx={{ fontSize: 18 }} />
        {unread > 0 && <span className="notif-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-dropdown-header">
            <span>Notifications</span>
            {unread > 0 && (
              <button className="notif-markall" onClick={handleMarkAll}>Mark all read</button>
            )}
          </div>
          <div className="notif-list">
            {!loaded ? (
              <div className="notif-empty">Loading…</div>
            ) : items.length === 0 ? (
              <div className="notif-empty">No notifications yet.</div>
            ) : (
              items.map(n => (
                <div
                  key={n.id}
                  className={`notif-item ${n.is_read ? '' : 'unread'}`}
                  onClick={() => handleItemClick(n)}
                >
                  <span className={`notif-status-dot notif-status-${n.status}`} />
                  <div className="notif-item-body">
                    <div className="notif-item-title">
                      {(() => {
                        const { context, statusLabel } = buildStatusParts(n);
                        return context ? (
                          <>Your feedback on <strong>{context}</strong> is now <strong>{statusLabel}</strong></>
                        ) : (
                          <>Your feedback is now <strong>{statusLabel}</strong></>
                        );
                      })()}
                    </div>
                    {n.note && <div className="notif-item-note">{n.note}</div>}
                    <div className="notif-item-time">{fmt(n.created_at)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;